import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChevronLeft, Timer, Trophy, AlertCircle, CheckCircle2, XCircle, ArrowRight, History, Loader2, Lock,
} from "lucide-react";

const LENGTH_OPTIONS = [
  { value: 50, label: "50 questions · short" },
  { value: 75, label: "75 questions · medium" },
  { value: 100, label: "100 questions · full" },
];
const DURATION_OPTIONS = [
  { value: 60, label: "60 minutes" },
  { value: 90, label: "90 minutes" },
  { value: 120, label: "120 minutes" },
];

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export default function MockExamHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [length, setLength] = useState(50);
  const [duration, setDuration] = useState(60);
  const [history, setHistory] = useState([]);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/student/mock-exam/history").then((r) => setHistory(r.data.history || [])).catch(() => {});
  }, []);

  const startExam = async () => {
    setStarting(true);
    try {
      const r = await api.post("/student/mock-exam/start", { length, duration_minutes: duration });
      // Hand the session payload to the runner via sessionStorage so we don't
      // re-fetch immediately (and so a quick refresh on the runner page still works).
      sessionStorage.setItem(`mock_exam_${r.data.session_id}`, JSON.stringify(r.data));
      nav(`/student/mock-exam/${r.data.session_id}`);
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 402) {
        toast.error(detail || "Mock Exam is a premium feature. Upgrade to unlock.", { duration: 5000 });
      } else {
        toast.error(detail || "Could not start mock exam");
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="mock-exam-home">
      <Link to="/student" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </Link>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-1.5">
          <Trophy className="w-3.5 h-3.5" /> Mock Exam Mode
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Simulate the real exam
        </h1>
        <p className="text-slate-600 mt-2">
          Timed, no hints, no rationale during the test. Get a full board-exam-style experience
          with per-subject breakdown after.
        </p>
      </div>

      <Card className="p-6 border-slate-200" data-testid="mock-exam-config-card">
        <h3 className="font-display font-bold text-lg mb-4">Configure your exam</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Length</label>
            <Select value={String(length)} onValueChange={(v) => setLength(parseInt(v, 10))}>
              <SelectTrigger className="mt-1.5 w-full" data-testid="mock-exam-length-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LENGTH_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)} data-testid={`mock-exam-length-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Duration</label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
              <SelectTrigger className="mt-1.5 w-full" data-testid="mock-exam-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)} data-testid={`mock-exam-duration-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5 p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm space-y-1.5" data-testid="mock-exam-rules">
          <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span><strong>Read these before you start:</strong></span></div>
          <ul className="ml-6 list-disc text-[13px] space-y-1">
            <li>The timer cannot be paused once you start.</li>
            <li>No AI hints, no rationale, no instant feedback during the exam.</li>
            <li>You can flag questions and jump back to them while the timer runs.</li>
            <li>Score is revealed only after you submit (or the timer hits zero).</li>
            <li>Passing score is <strong>75%</strong>.</li>
            <li>Questions are sampled <strong>proportionally</strong> across all subjects in your course — mirroring real board exam composition.</li>
          </ul>
        </div>

        <Button
          className="mt-5 w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold"
          onClick={startExam}
          disabled={starting}
          data-testid="mock-exam-start-btn"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Timer className="w-4 h-4 mr-2" />}
          Start Mock Exam
        </Button>
      </Card>

      {history.length > 0 && (
        <Card className="p-6 border-slate-200" data-testid="mock-exam-history-card">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" /> Past Attempts
          </h3>
          <div className="space-y-2">
            {history.map((h) => (
              <Link
                key={h.id}
                to={`/student/mock-exam/${h.id}/results`}
                className="flex items-center justify-between gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid={`mock-exam-history-${h.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">
                    {h.length}-question exam · {formatDuration(h.duration_seconds)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(h.completed_at).toLocaleString()}
                  </div>
                </div>
                <Badge
                  className={h.passed
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                    : "bg-rose-100 text-rose-800 border border-rose-300"}
                >
                  {h.passed ? "PASSED" : "FAILED"} · {h.accuracy_percentage}%
                </Badge>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
